import React from 'react'
import { PasswordRecoveryForm } from './components/auth/PasswordRecoveryForm'
import { AuthProvider } from './context/AuthContext'
import { RBACProvider } from './context/RBACContext'
import { ThemeProvider } from './context/ThemeContext'
import { AccessProvider } from './context/AccessContext'
import { AdminExperienceProvider } from './context/AdminExperienceContext'
import { I18nProvider } from './i18n/I18nContext'
import { AuthGuard } from './components/auth/AuthGuard'
import { AdminShell } from './components/layout/AdminShell'
import { marketingPageBySlug } from './components/landing/marketingPages'
import { NexusLandingPage } from './pages/NexusLandingPage'
import { MarketingLandingPage } from './pages/MarketingLandingPage'
import { DemoRequestPage } from './pages/DemoRequestPage'
import { SalesDeckIndexPage } from './pages/SalesDeckIndexPage'
import { SalesDeckPage } from './pages/SalesDeckPage'
import { ErrorBoundary } from './components/ui'
import { ErrorBoundaryProbe } from './components/ui/ErrorBoundaryProbe'

const localePrefixPattern = /^(en|ar|fr)(?:\/|$)/i

const getPublicRoutePath = () => {
  if (typeof window === 'undefined') return ''
  return window.location.pathname.replace(/^\/+|\/+$/g, '').replace(localePrefixPattern, '')
}

const isLandingRoute = () => {
  if (typeof window === 'undefined') return false
  const routePath = getPublicRoutePath()
  return routePath === '' || /^(?:raqib360|landing)$/i.test(routePath)
}

const getMarketingSlug = () => {
  const slug = getPublicRoutePath()
  return marketingPageBySlug.has(slug) ? slug : ''
}

export const App: React.FC = () => {
  const routePath = getPublicRoutePath()
  const marketingSlug = getMarketingSlug()
  const salesDeckMatch = routePath.match(/^sales-decks\/([^/]+)$/i)

  const publicPage = routePath === 'demo-request' ? <DemoRequestPage /> : routePath === 'sales-decks' ? <SalesDeckIndexPage /> : salesDeckMatch ? <SalesDeckPage slug={salesDeckMatch[1]} /> : marketingSlug ? <MarketingLandingPage slug={marketingSlug} /> : isLandingRoute() ? <NexusLandingPage /> : null

  const authenticatedApplication = (
    <ThemeProvider>
      <AuthProvider>
        <RBACProvider>
          <AuthGuard>
            <AccessProvider>
              <AdminExperienceProvider>
                <AdminShell />
              </AdminExperienceProvider>
            </AccessProvider>
          </AuthGuard>
        </RBACProvider>
      </AuthProvider>
    </ThemeProvider>
  )

  if (routePath === 'forgot-password' || routePath === 'reset-password') {
    return <I18nProvider><PasswordRecoveryForm complete={routePath === 'reset-password'} /></I18nProvider>
  }

  if (routePath === '__diag/error-boundary') {
    return <I18nProvider><ErrorBoundaryProbe /></I18nProvider>
  }

  if (routePath === 'login') {
    return <I18nProvider>{authenticatedApplication}</I18nProvider>
  }

  return (
    <I18nProvider>
      {publicPage ? (
      <ErrorBoundary resetKeys={[routePath]} message="This page could not be displayed. Please reload to try again.">
        {publicPage}
      </ErrorBoundary>
      ) : (
      authenticatedApplication
      )}
    </I18nProvider>
  )
}
